import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusSelect } from "@/components/StatusSelect";
import { PrioritySelect } from "@/components/PrioritySelect";
import { AssigneeSelect } from "@/components/AssigneeSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TaskLabels from "./TaskLabels";
import TaskGitHub from "./TaskGitHub";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { DueDateSelect } from "@/components/DueDateSelect";
import { FormSubmissionLink } from "@/components/ItemLink";
import { TbFileText, TbExternalLink } from 'react-icons/tb';
import { useState } from 'react';

export default function TaskSidebar({ task, onTaskChange, onRefreshActivities, organizationSlug, isLoading }) {
    const [weeksPopoverOpen, setWeeksPopoverOpen] = useState(false);
    
    // Helper to get the close activity - might want to move this to the backend
    const getCloseActivity = () => {
        if (!task?.closedAt || !task?.activities) return null;
        
        return task.activities.find(a => 
            a.type === 'task_closed' && 
            Math.abs(new Date(a.createdAt).getTime() - new Date(task.closedAt).getTime()) < 1000
        );
    };
    
    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-normal text-neutral-500 dark:text-neutral-600">Bot Insights</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-7">
                        {/* Status, Priority, Assignee skeletons */}
                        <div className="flex flex-col space-y-4">
                            <div className="h-7 w-24 bg-muted animate-pulse rounded-md" />
                            <div className="h-7 w-32 bg-muted animate-pulse rounded-md" />
                            <div className="h-7 w-28 bg-muted animate-pulse rounded-md" />
                        </div>
                
                        {/* Due Date skeleton */}
                        <div className="h-15">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Due Date</div>
                            <div className="h-7 w-24 bg-muted animate-pulse rounded-md" />
                        </div>

                        {/* Labels skeleton */}
                        <div className="h-15">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Labels</div>
                            <div className="h-7 w-32 bg-muted animate-pulse rounded-md" />
                        </div>

                        {/* Active Weeks skeleton */}
                        <div className="h-13">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600 mb-2">Active Weeks</div>
                            <div className="flex gap-2">
                                <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
                                <div className="h-6 w-20 bg-muted animate-pulse rounded-md" />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base font-normal text-neutral-500 dark:text-neutral-600">Bot Insights</CardTitle>
            </CardHeader>
            <CardContent>
                {/* Closed Banner */}
                {task.closedAt && (() => {
                    const closeActivity = getCloseActivity();
                    return (
                        <div className="mb-5 pb-5 border-b">
                            <div className="px-2.5 py-2 bg-violet-50 dark:bg-violet-950/20 rounded border border-violet-200 dark:border-violet-800/30">
                                <div className="text-xs text-violet-700 dark:text-violet-300 font-medium">
                                    Closed {formatDistanceToNow(new Date(task.closedAt), { addSuffix: true })}
                                    {closeActivity?.actor && (
                                        <span className="text-violet-600 dark:text-violet-400"> by {closeActivity.actor.name}</span>
                                    )}
                                </div>
                                {closeActivity?.comment?.content && (
                                    <TooltipProvider delayDuration={300}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="text-xs text-violet-600/60 dark:text-violet-400/60 mt-1 truncate">
                                                    {closeActivity.comment.content}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" align="start" className="max-w-xs">
                                                <p className="text-xs">{closeActivity.comment.content}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                        </div>
                    );
                })()}
                
                <div className="space-y-7">
                    <div className="flex flex-col space-y-4">
                        <StatusSelect
                            showLabel
                            task={task}
                            onTaskChange={onTaskChange}
                            onRefreshActivities={onRefreshActivities}
                        />
                        <PrioritySelect
                            showLabel
                            task={task}
                            onTaskChange={onTaskChange}
                            onRefreshActivities={onRefreshActivities}
                        />
                        <AssigneeSelect
                            showName
                            task={task}
                            organizationSlug={organizationSlug}
                            onTaskChange={onTaskChange}
                            onRefreshActivities={onRefreshActivities}
                        />
                    </div>

                    {/* Due Date */}
                    <DueDateSelect
                        task={task}
                        showLabel
                        organizationSlug={organizationSlug}
                        onTaskChange={onTaskChange}
                        onRefreshActivities={onRefreshActivities}
                    />

                    {/* Labels */}
                    <TaskLabels
                        task={task}
                        onTaskChange={onTaskChange}
                        organizationSlug={organizationSlug}
                        onRefreshActivities={onRefreshActivities}
                    />



                    {/* GitHub Integration */}
                    <TaskGitHub task={task} organizationSlug={organizationSlug} sidebarLoading={isLoading} onRefreshActivities={onRefreshActivities} />

                    {/* Active Weeks */}
                    <div className="space-y-2">
                        <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Active Weeks</div>
                        {task.weeks && task.weeks.length > 0 ? (
                            <Popover open={weeksPopoverOpen} onOpenChange={setWeeksPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <button className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left">
                                        {task.weeks.length <= 3 ? (
                                            task.weeks.map((week, index) => (
                                                <span key={index}>
                                                    Week {week.weekNumber}{index < task.weeks.length - 1 && ', '}
                                                </span>
                                            ))
                                        ) : (
                                            <>
                                                Week {task.weeks[0].weekNumber}
                                                <span className="px-1">...</span>
                                                Week {task.weeks[task.weeks.length - 1].weekNumber}
                                            </>
                                        )}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-2" align="start">
                                    <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                        {task.weeks.map((week, index) => (
                                            <div key={index} className="text-xs text-muted-foreground px-2 py-1.5 rounded hover:bg-muted/50">
                                                <span className="font-medium text-foreground">Week {week.weekNumber}</span>
                                                <span className="text-muted-foreground/70 ml-2">
                                                    {new Date(week.startDate).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })} – {new Date(week.endDate).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : (
                            <div className="text-xs text-muted-foreground/70">No active weeks</div>
                        )}
                    </div>

                    {/* Submission Origin */}
                    {task.createdFromSubmission && (
                        <div className="space-y-2">
                            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Originated From</div>
                            <FormSubmissionLink
                                submission={task.createdFromSubmission}
                                organizationSlug={organizationSlug}
                            />
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}